import { A } from '@solidjs/router';
import { For, type JSX } from 'solid-js';
import { t } from '../i18n';
import { orgHref } from '../lib/org';
import type { AccountSession } from '../lib/overview';
import { absolute, clockOrDay } from '../lib/time';

// Audit feed (Home, right of the fleet table — canvas APP artboard): the
// account's recent agent sessions from `/v1/sessions` + the `sessions` stream
// topic, newest-first. Presenter — each row is one mono line: the wall-clock
// stamp, the outcome-voiced event linking into the session's full audit
// timeline, and the repo · entity it touched. Its own `home-feed` block: the
// `home-audit` rows are `AuditFeedList`'s (the /audit archive), a different
// row shape that must not move when this one does.

/** Session outcome → feed voice; unrecognized outcomes render verbatim. */
function title(s: AccountSession): string {
  const key = `overview.audit_${s.outcome ?? 'running'}`;
  return t.has(key) ? t(key) : (s.outcome ?? t('overview.audit_running'));
}

/** Localized entity noun (issue/pr/repo/account/release); raw kind if unknown. */
function entityLabel(kind: string): string {
  const key = `overview.entity_${kind}`;
  return t.has(key) ? t(key) : kind;
}

/** "owner/repo · Issue #number" (#853); the number drops out when unresolved. */
function meta(s: AccountSession): string {
  const kind = entityLabel(s.entity_kind);
  const entity =
    s.entity_number === null ? kind : t('overview.entity_meta', { kind, number: s.entity_number });
  return `${s.repo_full_name} · ${entity}`;
}

export function LiveAuditFeed(props: { sessions: readonly AccountSession[] }): JSX.Element {
  return (
    <ul class="home-feed">
      <For each={props.sessions}>
        {(s) => (
          <li class="home-feed__row">
            <time class="home-feed__time" datetime={s.started_iso} title={absolute(s.started_iso)}>
              {clockOrDay(s.started_iso)}
            </time>
            <div class="home-feed__lead">
              <A class="home-feed__title" href={orgHref(`/audit/${s.id}`)}>
                {title(s)}
              </A>
              <span class="home-feed__meta">{meta(s)}</span>
            </div>
          </li>
        )}
      </For>
    </ul>
  );
}
